/**
 * Astronomical distance units and light-travel time.
 *
 * Definitions, all exact:
 * - the speed of light is 299 792 458 m/s (SI, since 1983);
 * - the astronomical unit is 149 597 870 700 m (IAU 2012 Resolution B2);
 * - the Julian year is 365.25 days of 86 400 s, and the light-year is the distance light
 *   travels in one: 299 792 458 × 31 557 600 = 9 460 730 472 580 800 m;
 * - the parsec is 648 000 / π astronomical units (IAU 2015 Resolution B2), the distance at
 *   which one astronomical unit subtends one arcsecond.
 */

import {
  ASTRONOMICAL_UNIT,
  JULIAN_YEAR_SECONDS,
  LIGHT_YEAR,
  SPEED_OF_LIGHT,
} from "@/lib/tool-kit/physics-fields/constants";
import type { UnitDef } from "@/lib/tool-kit/physics-fields/units";

/** One parsec in metres: 648 000 / π astronomical units. */
export const PARSEC = (648_000 / Math.PI) * ASTRONOMICAL_UNIT;

/** Metres in one international mile (exact). */
export const MILE = 1609.344;

export const DISTANCE_UNITS: readonly UnitDef[] = [
  { id: "m", symbol: "m", factor: 1 },
  { id: "km", symbol: "km", factor: 1000 },
  { id: "mi", symbol: "mi", factor: MILE },
  { id: "au", symbol: "AU", factor: ASTRONOMICAL_UNIT },
  { id: "ls", symbol: "ls", factor: SPEED_OF_LIGHT },
  { id: "lmin", symbol: "lmin", factor: SPEED_OF_LIGHT * 60 },
  { id: "lh", symbol: "lh", factor: SPEED_OF_LIGHT * 3600 },
  { id: "ld", symbol: "ld", factor: SPEED_OF_LIGHT * 86_400 },
  { id: "ly", symbol: "ly", factor: LIGHT_YEAR },
  { id: "pc", symbol: "pc", factor: PARSEC },
  { id: "kpc", symbol: "kpc", factor: PARSEC * 1e3 },
  { id: "mpc", symbol: "Mpc", factor: PARSEC * 1e6 },
];

/** Speeds, as a size in metres per second. */
export const SPEED_UNITS: readonly UnitDef[] = [
  { id: "kms", symbol: "km/s", factor: 1000 },
  { id: "kmh", symbol: "km/h", factor: 1000 / 3600 },
  { id: "mph", symbol: "mph", factor: MILE / 3600 },
  { id: "c", symbol: "c", factor: SPEED_OF_LIGHT },
];

export type DurationParts =
  | { kind: "years"; years: number }
  | {
      kind: "parts";
      days: number;
      hours: number;
      minutes: number;
      seconds: number;
    }
  | { kind: "seconds"; seconds: number };

/**
 * A span in seconds as years (one Julian year or more), as days, hours, minutes and
 * whole seconds (a minute up to a year), or as plain seconds.
 */
export function splitDuration(seconds: number): DurationParts {
  if (seconds >= JULIAN_YEAR_SECONDS) {
    return { kind: "years", years: seconds / JULIAN_YEAR_SECONDS };
  }
  if (seconds < 60) {
    return { kind: "seconds", seconds };
  }
  let rest = Math.round(seconds);
  const days = Math.floor(rest / 86_400);
  rest -= days * 86_400;
  const hours = Math.floor(rest / 3600);
  rest -= hours * 3600;
  const minutes = Math.floor(rest / 60);
  return { kind: "parts", days, hours, minutes, seconds: rest - minutes * 60 };
}

/** Light-travel time over a distance in metres, in seconds. */
export function lightTime(metres: number): number {
  return metres / SPEED_OF_LIGHT;
}
